package com.flowyride.data.speedcameras.datagouv

import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.SpeedCameraRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.http.isSuccess
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.withContext
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.sync.withPermit
import kotlin.coroutines.cancellation.CancellationException

/**
 * Downloads the whole list once (≈ 3 300 cameras, 17 pages of 200) and keeps it in memory.
 * Coordinates are stored as text by the API, so no server-side bounding-box filter is possible.
 *
 * One failed page would lose the whole list: pages are retried, and fetched a few at a time.
 * A failed download is not cached, so the next call tries again.
 */
class DataGouvSpeedCameraRepository(
    private val client: HttpClient,
    private val config: DataGouvConfig = DataGouvConfig(),
) : SpeedCameraRepository {

    private val mutex = Mutex()
    private val concurrentPages = Semaphore(MAX_CONCURRENT_PAGES)
    private var cache: List<SpeedCamera>? = null

    override suspend fun all(): List<SpeedCamera> = mutex.withLock {
        cache ?: download().also { cache = it }
    }

    private suspend fun download(): List<SpeedCamera> = coroutineScope {
        val first = page(1)
        val pageCount = (first.meta.total + config.pageSize - 1) / config.pageSize
        val others = (2..pageCount).map { async { concurrentPages.withPermit { page(it) } } }.awaitAll()
        withContext(Dispatchers.Default) {
            (listOf(first) + others)
                .flatMap { it.data }
                .mapNotNull { it.toSpeedCameraOrNull() }
        }
    }

    private suspend fun page(number: Int): TabularPage {
        var attempt = 1
        while (true) {
            try {
                return fetchPage(number)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                if (attempt >= MAX_ATTEMPTS) throw e
                delay(RETRY_DELAY_MS * attempt)
                attempt++
            }
        }
    }

    private suspend fun fetchPage(number: Int): TabularPage {
        val response = client.get("${config.baseUrl.trimEnd('/')}/api/resources/${config.resourceId}/data/") {
            parameter("page", number)
            parameter("page_size", config.pageSize)
        }
        check(response.status.isSuccess()) { "Speed cameras: HTTP ${response.status.value} on page $number" }
        return response.body()
    }

    private companion object {
        const val MAX_CONCURRENT_PAGES = 4
        const val MAX_ATTEMPTS = 3
        const val RETRY_DELAY_MS = 500L
    }
}
